import { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { API } from "@/lib/api";
import { Btn, Card, Label, Input, Textarea, Select } from "@/components/shared";
import { Attachment } from "@/components/shared";
import { Sparkles, ArrowLeft, CheckCircle2, HelpCircle } from "lucide-react";
import { HowItWorksModal } from "@/components/HowItWorks";
import { MediaUploader } from "@/components/MediaUploader";
import { Lightbox } from "@/components/Lightbox";

const SERVICE_TYPES = ["Property Preservation", "Initial Services", "Inspection", "Occupancy Check", "Eviction", "REO Services", "Maid Refresh / Janitorial", "Lock Changes / Re-Key", "Debris Removal", "Winterization", "De-Winterization", "Grass Cut / Lawn Maintenance", "Securing / Board-Up", "Pool Securing", "Repairs", "Virtual Tour", "Mobile Notary", "Real Estate Photography", "Construction"];
const CLIENT_TYPES = ["Homeowner", "Realtor", "Investor", "Property Manager", "Bank/REO"];

const money = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export default function PublicRequest() {
  const [form, setForm] = useState({ contact_name: "", contact_email: "", contact_phone: "", client_type: "Homeowner", service_type: SERVICE_TYPES[0], address: "", notes: "", scope_of_work: "", email_consent: true, sms_consent: false });
  const [photoIds, setPhotoIds] = useState([]);
  const [lb, setLb] = useState(null);
  const [lineItems, setLineItems] = useState([]);
  const [busy, setBusy] = useState(false);
  const [aiBusy, setAiBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [howOpen, setHowOpen] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const estimateTotal = lineItems.reduce((s, it) => s + Number(it.quantity || 0) * Number(it.unit_price || 0), 0);

  const uploadFile = async (f) => {
    const fd = new FormData(); fd.append("file", f);
    const { data } = await axios.post(`${API}/public/upload`, fd, { headers: { "Content-Type": "multipart/form-data" } });
    return { id: data.id };
  };

  const estimate = async () => {
    if (!form.service_type) { toast.error("Pick a type of work first"); return; }
    setAiBusy(true);
    try {
      const { data } = await axios.post(`${API}/public/ai/photo-scope`, { photo_ids: photoIds, service_type: form.service_type, client_type: form.client_type, notes: form.notes });
      if (data.scope) setForm((f) => ({ ...f, scope_of_work: data.scope }));
      setLineItems(Array.isArray(data.line_items) ? data.line_items : []);
      toast.success("Instant estimate ready");
    } catch { toast.error("AI is busy, please try again"); }
    finally { setAiBusy(false); }
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await axios.post(`${API}/public/requests`, { ...form, photo_ids: photoIds, estimate_line_items: lineItems, estimate_total: estimateTotal });
      setDone(true);
    } catch { toast.error("Could not submit — please check your details"); }
    finally { setBusy(false); }
  };

  if (done) return (
    <Shell>
      <Card className="p-8 text-center bg-[#141417]">
        <CheckCircle2 className="mx-auto text-[#FF5722]" size={44} />
        <h1 className="font-head font-black uppercase text-2xl text-white mt-4">Request Received</h1>
        <p className="text-zinc-200 mt-2">Thanks, {form.contact_name}. Our dispatch team will review your estimate and reach out to {form.contact_email}{form.contact_phone ? ` / ${form.contact_phone}` : ""} to confirm your final quote.</p>
        <Link to="/login" className="inline-block mt-6"><Btn variant="secondary">Back to sign in</Btn></Link>
      </Card>
    </Shell>
  );

  return (
    <Shell>
      <div className="flex items-center justify-between mb-4">
        <Link to="/login" className="flex items-center gap-1 text-zinc-500 hover:text-[#FF5722] font-mono text-sm uppercase"><ArrowLeft size={16} /> Sign in</Link>
        <button type="button" data-testid="pr-how-btn" onClick={() => setHowOpen(true)} className="flex items-center gap-1 text-zinc-400 hover:text-[#FF5722] font-mono text-sm uppercase"><HelpCircle size={16} /> How it works</button>
      </div>
      <Card className="p-6 sm:p-8 animate-rise">
        <div className="font-mono text-xs uppercase tracking-widest text-[#FF5722] mb-1">No account needed</div>
        <h1 className="font-head font-black uppercase text-3xl text-white tracking-tight">Request Service</h1>
        <p className="text-zinc-400 text-sm mt-2">Get an instant AI estimate, then submit for review. We'll email and call you back to confirm your final quote.</p>
        <form onSubmit={submit} className="space-y-4 mt-6">
          <div className="grid sm:grid-cols-2 gap-4">
            <div><Label>Your Name</Label><Input data-testid="pr-name" value={form.contact_name} onChange={set("contact_name")} required /></div>
            <div><Label>Email</Label><Input data-testid="pr-email" type="email" value={form.contact_email} onChange={set("contact_email")} required /></div>
            <div><Label>Phone (for call back)</Label><Input data-testid="pr-phone" value={form.contact_phone} onChange={set("contact_phone")} /></div>
            <div><Label>I am a</Label><Select data-testid="pr-client-type" value={form.client_type} onChange={set("client_type")}>{CLIENT_TYPES.map((c) => <option key={c}>{c}</option>)}</Select></div>
            <div><Label>Type of Work</Label><Select data-testid="pr-service" value={form.service_type} onChange={set("service_type")}>{SERVICE_TYPES.map((s) => <option key={s}>{s}</option>)}</Select></div>
            <div><Label>Property Address</Label><Input data-testid="pr-address" value={form.address} onChange={set("address")} required /></div>
          </div>
          <div><Label>Notes</Label><Textarea data-testid="pr-notes" value={form.notes} onChange={set("notes")} placeholder="Describe what you need…" /></div>
          <div>
            <Label>Photos / Documents (images or PDF)</Label>
            <MediaUploader allowPdf upload={uploadFile} onUploaded={(ids) => setPhotoIds((p) => [...p, ...ids])} />
            {photoIds.length > 0 && (
              <div className="flex items-center gap-2 flex-wrap mt-2">
                {photoIds.map((pid, i) => <Attachment key={pid} src={`${API}/public/files/${pid}`} size={64} onClick={() => setLb(i)} className="object-cover border-2 border-zinc-800" />)}
              </div>
            )}
          </div>

          <div className="bg-[#09090b] border-2 border-[#FF5722]/40 p-4">
            <div className="flex items-center justify-between gap-2">
              <div className="font-head font-bold uppercase text-white text-sm tracking-wide flex items-center gap-1.5"><Sparkles size={15} className="text-[#FF5722]" /> Instant Estimate</div>
              <button type="button" data-testid="pr-estimate-btn" onClick={estimate} disabled={aiBusy} className="bg-[#FF5722] text-black border-2 border-black shadow-[3px_3px_0px_#000] font-head font-bold uppercase text-xs px-3 py-2 disabled:opacity-50">{aiBusy ? "Calculating…" : "Get Instant Estimate"}</button>
            </div>
            {lineItems.length > 0 && (
              <div className="mt-3" data-testid="pr-estimate-result">
                <div className="divide-y divide-zinc-800 border border-zinc-800">
                  {lineItems.map((it, idx) => (
                    <div key={`${it.name}-${idx}`} className="flex items-center justify-between gap-2 px-3 py-2 text-sm">
                      <span className="text-zinc-200">{it.name} <span className="text-zinc-500 font-mono text-xs">×{it.quantity}{it.unit ? ` ${it.unit}` : ""}</span></span>
                      <span className="text-white font-mono">{money(Number(it.quantity || 0) * Number(it.unit_price || 0))}</span>
                    </div>
                  ))}
                </div>
                <div className="flex items-center justify-between px-3 py-2 mt-2 bg-[#FF5722] text-black font-head font-black uppercase" data-testid="pr-estimate-total">
                  <span>Estimated Total</span><span>{money(estimateTotal)}</span>
                </div>
                <p className="text-[11px] text-zinc-500 mt-2 leading-snug">This is an automated estimate based on national &amp; standard pricing. It is <span className="text-zinc-300 font-semibold">not a final quote</span> — your final price will be confirmed by our team after review.</p>
              </div>
            )}
          </div>

          <div>
            <Label>Scope of Work</Label>
            <Textarea data-testid="pr-scope" value={form.scope_of_work} onChange={set("scope_of_work")} placeholder="AI drafts this from your photos & details, or write it yourself" />
          </div>
          <label className="flex items-center gap-2 text-sm text-zinc-300"><input type="checkbox" data-testid="pr-email-consent" checked={form.email_consent} onChange={(e) => setForm({ ...form, email_consent: e.target.checked })} /> Email me updates & replies to this request</label>
          <label className="flex items-center gap-2 text-sm text-zinc-300"><input type="checkbox" data-testid="pr-sms-consent" checked={form.sms_consent} onChange={(e) => setForm({ ...form, sms_consent: e.target.checked })} /> Text me order updates (SMS)</label>
          <Btn data-testid="pr-submit" type="submit" disabled={busy} className="w-full">{busy ? "Submitting…" : "Submit Request"}</Btn>
        </form>
      </Card>
      <Lightbox items={photoIds.map((pid) => ({ src: `${API}/public/files/${pid}`, name: `attachment_${pid}` }))} index={lb} setIndex={setLb} onClose={() => setLb(null)} />
      <HowItWorksModal open={howOpen} onClose={() => setHowOpen(false)} />
    </Shell>
  );
}

function Shell({ children }) {
  return <div className="min-h-screen bg-[#09090b] py-8 px-4"><div className="max-w-2xl mx-auto">{children}</div></div>;
}
